// Things waiting for the operator: the one set of rules behind the sidebar
// badges, the Home attention row and (as their owners switch over) the
// Meetings, Skills, Code and Todos screens, so every surface
// shows the same numbers.
//
// Pure functions, no React. Copied, not moved, from where the screens
// compute them today:
//   meetings   features/meetings/MeetingsPanel.tsx  "Needs attention" bucket
//   skills     features/skills/SkillsPanel.tsx      counts.pending
//   deploys    features/cc-deploys/CCDeploysList.tsx groupBySlug + "broken"
//   todos      features/todos/TodosPanel.tsx        dueLabel, on LOCAL dates

import type { CCDeploySummary } from "@/api/ccSessions";
import type { Meeting } from "@/api/meetings";
import type { LearnedSkill } from "@/api/skills";
import type { WorkshopProposal } from "@/api/skillWorkshop";
import type { Todo } from "@/api/todos";
import { dayKey, daysBetween, todayKey, toDate } from "@/lib/format";

// ─── meetings ───

/** "Recent" = at most this many local calendar days old. */
export const RECENT_MEETING_DAYS = 7;

/**
 * The meeting needs the operator: it ended and nobody has written it up,
 * the agent asked in chat and is waiting (prompted), or the notes are done
 * but an action is still open.
 */
export function meetingNeedsYou(m: Pick<Meeting, "status" | "actions">): boolean {
  return (
    m.status === "ended" ||
    m.status === "prompted" ||
    (m.status === "processed" && m.actions.some((a) => a.status === "open"))
  );
}

/** The agent asked about this meeting in chat and is waiting for notes (any age). */
export function meetingWaitingForNotes(m: Pick<Meeting, "status">): boolean {
  return m.status === "prompted";
}

/**
 * A question the agent asked in chat waits on you for this many whole
 * local calendar days: a Friday 17:00 question still counts on Monday
 * and drops on Tuesday. After that it is a question nobody answered
 * (meetingAskedNoReply), listed folded and counted nowhere.
 */
export const ASKED_WAIT_DAYS = 3;

/**
 * Whole local calendar days since the agent asked about the meeting
 * (prompted_at, else the meeting's end, else its start, else when it was
 * logged), the same day arithmetic as meetingAgeDays. 0 when none of them
 * reads as a date; negative for a time in the future.
 */
export function meetingAskedDays(
  m: Pick<Meeting, "prompted_at" | "end_at" | "start_at" | "created_at">,
  now: Date = new Date(),
): number {
  const when = toDate(m.prompted_at ?? m.end_at ?? m.start_at ?? m.created_at);
  return when ? daysBetween(when, now) : 0;
}

/** The agent asked about it in chat at most ASKED_WAIT_DAYS days ago and waits for notes. */
export function meetingAskedRecently(
  m: Pick<Meeting, "status" | "prompted_at" | "end_at" | "start_at" | "created_at">,
  now: Date = new Date(),
): boolean {
  return meetingWaitingForNotes(m) && meetingAskedDays(m, now) <= ASKED_WAIT_DAYS;
}

/**
 * Its in-app recording failed (upload or transcription) and you have not
 * dismissed the meeting: only you can retry it or let it go. No age limit.
 */
export function meetingRecordingFailed(m: Pick<Meeting, "status" | "recording_status">): boolean {
  return m.recording_status === "failed" && m.status !== "skipped";
}

type WaitFields = Pick<Meeting, "status" | "recording_status" | "prompted_at" | "end_at" | "start_at" | "created_at">;

/**
 * The meeting WAITS ON YOU: the agent asked about it in chat at most
 * ASKED_WAIT_DAYS days ago and is waiting for your notes, or its
 * recording failed (and it was not dismissed). The one count behind the
 * sidebar, Home, the Meetings tab and the phone (and the server's
 * companion summary, services/companion_summary.py). Ended meetings
 * nobody has asked about yet (the agent will ask), open actions (the
 * backlog), older meetings and questions asked longer ago than that
 * (meetingAskedNoReply) do not count.
 */
export function meetingWaitsOnYou(m: WaitFields, now: Date = new Date()): boolean {
  return meetingAskedRecently(m, now) || meetingRecordingFailed(m);
}

/**
 * The agent asked about it in chat more than ASKED_WAIT_DAYS days ago and
 * nobody replied: it still needs you in the loose sense (meetingNeedsYou)
 * but no longer waits on you. A failed recording keeps a meeting waiting
 * on you, so such a meeting is never also here.
 */
export function meetingAskedNoReply(m: WaitFields, now: Date = new Date()): boolean {
  return meetingWaitingForNotes(m) && !meetingWaitsOnYou(m, now);
}

/**
 * Whole local calendar days since the meeting (its end, else its start,
 * else when it was logged). 0 for today and for anything in the future.
 */
export function meetingAgeDays(
  m: Pick<Meeting, "end_at" | "start_at" | "created_at">,
  now: Date = new Date(),
): number {
  const when = toDate(m.end_at ?? m.start_at ?? m.created_at);
  return when ? Math.max(0, daysBetween(when, now)) : 0;
}

export function isRecentMeeting(
  m: Pick<Meeting, "end_at" | "start_at" | "created_at">,
  now: Date = new Date(),
): boolean {
  return meetingAgeDays(m, now) <= RECENT_MEETING_DAYS;
}

export interface MeetingAttention {
  /** Every meeting that needs the operator (recent + older). */
  needsYou: number;
  /** ...of which at most RECENT_MEETING_DAYS old. */
  recent: number;
  /** ...of which older than that. */
  older: number;
  /** Meetings the agent asked about in chat at most ASKED_WAIT_DAYS days
   *  ago (meetingAskedRecently): the asked-in-chat part of waitingOnYou. */
  waitingForNotes: number;
  /**
   * Meetings that wait on you (meetingWaitsOnYou), over ALL meetings: a
   * failed recording counts even when the meeting does not need you
   * otherwise. THE count the sidebar, Home and the phone show.
   */
  waitingOnYou: number;
  /** ...the rest of waitingOnYou: the recording failed and the agent has
   *  not asked about it lately, so waitingForNotes + recordingFailed =
   *  waitingOnYou. */
  recordingFailed: number;
}

/**
 * Counts over the rows the caller has. useMeetings loads the newest 300
 * (upcoming first), so an old backlog beyond that is not counted.
 */
export function countMeetingAttention(
  meetings: readonly Meeting[],
  now: Date = new Date(),
): MeetingAttention {
  const out: MeetingAttention = {
    needsYou: 0,
    recent: 0,
    older: 0,
    waitingForNotes: 0,
    waitingOnYou: 0,
    recordingFailed: 0,
  };
  for (const m of meetings) {
    if (meetingWaitsOnYou(m, now)) {
      out.waitingOnYou += 1;
      if (meetingAskedRecently(m, now)) out.waitingForNotes += 1;
      else out.recordingFailed += 1;
    }
    if (!meetingNeedsYou(m)) continue;
    out.needsYou += 1;
    if (isRecentMeeting(m, now)) out.recent += 1;
    else out.older += 1;
  }
  return out;
}

// ─── skills ───

/**
 * A skill an agent proposed with OpenClaw's skill workshop that has not
 * been promoted yet. Promoted ones are already in the review queue, so
 * counting them here would count them twice.
 */
export function isOpenWorkshopProposal(p: Pick<WorkshopProposal, "promotedSkillId">): boolean {
  return p.promotedSkillId == null;
}

/** Skills waiting for review: the Tier 2 queue plus open workshop proposals. */
export function countPendingSkills(
  pending: readonly LearnedSkill[],
  proposals: readonly Pick<WorkshopProposal, "promotedSkillId">[],
): number {
  return pending.length + proposals.filter(isOpenWorkshopProposal).length;
}

// ─── deployments ───

export interface DeploySlugGroup {
  slug: string;
  latest: CCDeploySummary;
  thumbnailSource: CCDeploySummary;
  spawns: CCDeploySummary[];
  history: CCDeploySummary[];
}

/** The app's slug from its /agent-dev/<customer>/<slug>/ URL. */
export function deploySlug(d: Pick<CCDeploySummary, "url">): string {
  const m = d.url.match(/\/agent-dev\/[^/]+\/([^/]+)\//);
  return m?.[1] ?? "(legacy)";
}

/** Rows written by the retired reverify timer; not deploys of their own. */
export function isReverifyDeploy(d: Pick<CCDeploySummary, "app_path">): boolean {
  return d.app_path === "(reverify)";
}

/** One group per app, newest deploy first; reverify rows are dropped. */
export function groupDeploysBySlug(deploys: readonly CCDeploySummary[]): DeploySlugGroup[] {
  const groups = new Map<string, DeploySlugGroup>();
  for (const d of deploys) {
    if (isReverifyDeploy(d)) continue;
    const slug = deploySlug(d);
    let g = groups.get(slug);
    if (!g) {
      g = { slug, latest: d, thumbnailSource: d, spawns: [], history: [] };
      groups.set(slug, g);
    }
    g.history.push(d);
    g.spawns.push(d);
    if (Date.parse(d.spawned_at) > Date.parse(g.latest.spawned_at)) g.latest = d;
    if (d.screenshot_path && Date.parse(d.spawned_at) > Date.parse(g.thumbnailSource.spawned_at)) {
      g.thumbnailSource = d;
    }
    if (!g.thumbnailSource.screenshot_path && d.screenshot_path) g.thumbnailSource = d;
  }
  return Array.from(groups.values()).sort(
    (a, b) => Date.parse(b.latest.spawned_at) - Date.parse(a.latest.spawned_at),
  );
}

/**
 * The app's latest deploy has not passed its check: failing or not
 * verified (see below). Worth a look, but NOT necessarily down - use
 * isDeployFailing / isDeployUnverified for the words and the colour.
 */
export function isDeployDown(g: Pick<DeploySlugGroup, "latest">): boolean {
  return g.latest.overall_verdict !== "pass";
}

/** Something is broken: the check failed, or the app did not start. */
export function isDeployFailing(g: Pick<DeploySlugGroup, "latest">): boolean {
  const v = g.latest.overall_verdict;
  return v === "fail" || v === "spawn_failed";
}

/**
 * Not passed and not failed: the check was inconclusive, never finished
 * or is still running. The app may well be up - "Not verified", never
 * "down" or "failed" (the honesty rule in cc-deploys/deployStatus.ts).
 */
export function isDeployUnverified(g: Pick<DeploySlugGroup, "latest">): boolean {
  return isDeployDown(g) && !isDeployFailing(g);
}

export interface DeploymentCounts {
  /** Apps that need a look: failing + unverified. */
  down: number;
  failing: number;
  unverified: number;
  total: number;
}

export function countDeployments(deploys: readonly CCDeploySummary[]): DeploymentCounts {
  const groups = groupDeploysBySlug(deploys);
  const failing = groups.filter(isDeployFailing).length;
  const unverified = groups.filter(isDeployUnverified).length;
  return { down: failing + unverified, failing, unverified, total: groups.length };
}

export interface DeployAttention {
  count: number;
  /** danger when anything is failing, else warning. */
  tone: "danger" | "warning";
  /** "1 app failing", "2 apps not verified", "1 app failing, 1 not verified". */
  label: string;
  /** Title after the count on Home: "App failing", "Apps not verified", "Apps to check". */
  title: string;
}

/**
 * The words and colour for apps that need a look, shared by the sidebar
 * badge, Home, the Agents card and the Code tab count so the same app
 * reads the same everywhere. null when every app passed.
 */
export function deployAttention(c: Pick<DeploymentCounts, "failing" | "unverified">): DeployAttention | null {
  const count = c.failing + c.unverified;
  if (count === 0) return null;
  // Agent-built web apps read "App"/"Apps" in every workspace: "Deployments" means workflows as APIs.
  const noun = (n: number) => (n === 1 ? "app" : "apps");
  const Noun = (n: number) => (n === 1 ? "App" : "Apps");
  if (c.failing && c.unverified) {
    return {
      count,
      tone: "danger",
      label: `${c.failing} ${noun(c.failing)} failing, ${c.unverified} not verified`,
      title: "Apps to check",
    };
  }
  return c.failing
    ? { count, tone: "danger", label: `${count} ${noun(count)} failing`, title: `${Noun(count)} failing` }
    : { count, tone: "warning", label: `${count} ${noun(count)} not verified`, title: `${Noun(count)} not verified` };
}

// ─── apps (Code › Apps, where the workspace has Apps on; apps spec §10.6) ───
//
// Under the Apps switch every badge reads GET …/apps/summary instead of the
// legacy deploy list above (which only sees /agent-dev/<c>/<slug>/ routes,
// so never a new app); the legacy counts then feed only Code › Apps' "Older
// apps" heading. Only what waits on the operator counts: an app that is
// down, one that needs a yes, one with a question (building is not waiting).
// Danger when any app is down, else warning; the words always say which.

export interface AppsCounts {
  down: number;
  waiting_yes: number;
  asking: number;
}

export interface AppsPart {
  key: "down" | "waiting_yes" | "asking";
  count: number;
  /** After the number: "app down", "apps need your yes", "app has a question". */
  words: string;
  tone: "danger" | "warning";
}

/** Each kind of waiting that is there, loudest first. */
export function appsParts(c: AppsCounts): AppsPart[] {
  const parts: AppsPart[] = [];
  const one = (n: number, a: string, b: string) => (n === 1 ? a : b);
  if (c.down > 0) parts.push({ key: "down", count: c.down, words: one(c.down, "app down", "apps down"), tone: "danger" });
  if (c.waiting_yes > 0) {
    parts.push({ key: "waiting_yes", count: c.waiting_yes, words: one(c.waiting_yes, "app needs your yes", "apps need your yes"), tone: "warning" });
  }
  if (c.asking > 0) parts.push({ key: "asking", count: c.asking, words: one(c.asking, "app has a question", "apps have a question"), tone: "warning" });
  return parts;
}

/**
 * The apps that wait on the operator in the shape deployAttention gives
 * (count, tone, label, title), so the sidebar, Home and the Code tab draw
 * them the same way: "1 app down", "1 app down, 2 apps need your yes".
 * null when nothing waits (or the summary has not answered).
 */
export function appsAttention(c: AppsCounts | null | undefined): DeployAttention | null {
  if (!c) return null;
  const parts = appsParts(c);
  if (parts.length === 0) return null;
  const count = parts.reduce((n, p) => n + p.count, 0);
  const label = parts.map((p) => `${p.count} ${p.words}`).join(", ");
  const only = parts.length === 1 ? parts[0].words : null;
  const title = only ? only.charAt(0).toUpperCase() + only.slice(1) : "Apps need you";
  return { count, tone: c.down > 0 ? "danger" : "warning", label, title };
}

/**
 * Which list a badge reads (apps spec §10.6): apps/summary under the Apps
 * switch, the legacy deploy list where it is off, neither while the switch
 * is unknown (so a badge never shows one and then the other).
 */
export function appsBadgeSource(appsFlag: boolean | undefined): "apps" | "legacy" | null {
  return appsFlag === true ? "apps" : appsFlag === false ? "legacy" : null;
}

/** The Code tab's spoken count: "1 app needs you", "2 apps need you". */
export function appsNeedYouLabel(n: number): string {
  return `${n} ${n === 1 ? "app needs" : "apps need"} you`;
}

// ─── todos ───

/**
 * A todo's due day as a LOCAL "YYYY-MM-DD" key, "" when it has none.
 * due_at is usually date-only, which new Date() would read as UTC
 * midnight (and toISOString().slice(0, 10) gives the UTC day, which is
 * yesterday between 00:00 and 01:00 BST).
 */
export function todoDueKey(t: Pick<Todo, "due_at">): string {
  if (!t.due_at) return "";
  // Read the date as written, the way the server's Today view and the Todos
  // screen do (due_at[:10]); only fall back to parsing for other shapes.
  return /^\d{4}-\d{2}-\d{2}/.exec(t.due_at)?.[0] ?? dayKey(t.due_at) ?? "";
}

/** Open and due before today (local). */
export function isTodoOverdue(t: Pick<Todo, "due_at" | "done_at">, now: Date = new Date()): boolean {
  const due = todoDueKey(t);
  return !t.done_at && due !== "" && due < todayKey(now);
}

/** Open and due today (local); overdue items are not "due today". */
export function isTodoDueToday(t: Pick<Todo, "due_at" | "done_at">, now: Date = new Date()): boolean {
  return !t.done_at && todoDueKey(t) === todayKey(now);
}

export function countTodosDue(
  todos: readonly Pick<Todo, "due_at" | "done_at">[],
  now: Date = new Date(),
): { dueToday: number; overdue: number } {
  let dueToday = 0;
  let overdue = 0;
  for (const t of todos) {
    if (isTodoOverdue(t, now)) overdue += 1;
    else if (isTodoDueToday(t, now)) dueToday += 1;
  }
  return { dueToday, overdue };
}
